import { type TenantProvisioningContextDTO, TenantSeedStatus } from '@today/types'
import { Badge, type BadgeProps, Button } from '@today/ui-primitives'

/**
 * Props for {@link ProvisioningStatusPanelView}.
 *
 * @property rows - One entry per seeding context, already ordered by the server.
 * @property canRetry - Whether the caller may retry provisioning; hides the Retry action when false.
 * @property retrying - True while a retry request is in flight; disables Retry.
 * @property onRetry - Called when the user asks to retry provisioning.
 * @property error - A message to show above the table (e.g. a failed retry).
 */
export interface ProvisioningStatusPanelViewProps {
	rows: readonly TenantProvisioningContextDTO[]
	canRetry: boolean
	retrying: boolean
	onRetry: () => void
	error?: string | null
}

const STATUS: Record<TenantSeedStatus, { label: string; variant: NonNullable<BadgeProps['variant']> }> = {
	[TenantSeedStatus.PENDING]: { label: 'Pending', variant: 'muted' },
	[TenantSeedStatus.COMPLETED]: { label: 'Completed', variant: 'success' },
	[TenantSeedStatus.FAILED]: { label: 'Failed', variant: 'danger' },
}

/** The outcome column: counts when completed, the error when failed, nothing while pending. */
function Outcome({ row }: { row: TenantProvisioningContextDTO }) {
	if (row.status === TenantSeedStatus.COMPLETED) {
		return (
			<span>
				{row.itemsCreated} created · {row.itemsSkipped} skipped
			</span>
		)
	}
	if (row.status === TenantSeedStatus.FAILED) {
		return (
			<span className="flex flex-col">
				<code className="text-ds-sm">{row.errorCode}</code>
				<span className="text-ds-text-muted">{row.message}</span>
			</span>
		)
	}
	return null
}

/** One row per context: context, status, version, report time and outcome. */
function ProvisioningStatusTable({ rows }: { rows: readonly TenantProvisioningContextDTO[] }) {
	return (
		<table className="w-full text-left text-ds-sm">
			<thead>
				<tr className="text-ds-text-muted">
					<th scope="col">Context</th>
					<th scope="col">Status</th>
					<th scope="col">Version</th>
					<th scope="col">Reported</th>
					<th scope="col">Outcome</th>
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.context} className="border-t border-ds-border align-top">
						<td className="py-2 font-medium text-ds-text">{row.context}</td>
						<td className="py-2">
							<Badge variant={STATUS[row.status].variant} size="sm">
								{STATUS[row.status].label}
							</Badge>
						</td>
						<td className="py-2">{row.seederVersion === null ? '' : `v${row.seederVersion}`}</td>
						<td className="py-2">{row.reportedAt ? new Date(row.reportedAt).toLocaleString() : ''}</td>
						<td className="py-2">
							<Outcome row={row} />
						</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

/**
 * A tenant's provisioning status (TO-375): one row per bounded context that is
 * expected to seed the tenant or has reported, with its outcome, and a Retry
 * that re-runs every seeder idempotently. Pure — rows and callbacks are props.
 */
export function ProvisioningStatusPanelView({
	rows,
	canRetry,
	retrying,
	onRetry,
	error,
}: ProvisioningStatusPanelViewProps) {
	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between gap-4">
				<p className="text-ds-sm text-ds-text-muted">
					A failed context is a warning only — the tenant stays usable. Retrying re-runs every seeder; completed data is
					never duplicated or overwritten.
				</p>
				{canRetry ? (
					<Button variant="secondary" size="sm" isDisabled={retrying} onPress={onRetry}>
						{retrying ? 'Retrying…' : 'Retry provisioning'}
					</Button>
				) : null}
			</div>
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			{rows.length === 0 ? (
				<p className="text-ds-sm text-ds-text-muted">No seeders are registered yet</p>
			) : (
				<ProvisioningStatusTable rows={rows} />
			)}
		</div>
	)
}
